// @ts-nocheck
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTeamRole } from '@/hooks/useTeamRole';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import TeamRegistrationsList from './TeamRegistrationsList';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Props {
  team: any;
}

export default function TeamBillingView({ team }: Props) {
  const { data: role } = useTeamRole(team.id);
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: contactRow } = useQuery({
    queryKey: ['team-billing-contact', team.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('v_teams_with_contacts')
        .select('*')
        .eq('id', team.id)
        .maybeSingle();
      return data;
    },
  });

  const billingUserId = contactRow?.billing_user_id;

  const { data: profile } = useQuery({
    queryKey: ['profile', billingUserId],
    enabled: !!billingUserId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('profiles')
        .select('id, name, email, avatar_url')
        .eq('id', billingUserId)
        .maybeSingle();
      return data;
    },
  });

  const { data: members = [] } = useQuery({
    queryKey: ['team-members-active', team.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('team_members')
        .select('user_id, role, profile:profiles(id, name, email, avatar_url)')
        .eq('team_id', team.id)
        .eq('status', 'active');
      return data ?? [];
    },
  });

  const { data: regData } = useQuery({
    queryKey: ['team-registrations', team.id],
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction('rle-list-team-registrations', {
        body: { team_id: team.id },
      });
      if (error) throw error;
      return data;
    },
  });

  const summary = regData?.data?.summary ?? { total_registered: 0, total_paid: 0, total_outstanding: 0 };

  const payUrl = `${window.location.origin}/pay/${team.id}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(payUrl);
    toast.success('Link copied');
  };

  const handleChangeBilling = async () => {
    if (!selectedUserId) return;
    const { error } = await invokeEdgeFunction('rle-set-primary-billing', {
      body: { team_id: team.id, new_primary_billing_user_id: selectedUserId },
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Billing contact updated');
    setDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ['team-billing-contact', team.id] });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Billing contact</CardTitle>
        </CardHeader>
        <CardContent>
          {!profile ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={profile.avatar_url} />
                  <AvatarFallback>{profile.name?.[0] ?? '?'}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium">{profile.name}</div>
                  <div className="text-sm text-muted-foreground">{profile.email}</div>
                </div>
                <Badge variant="secondary">Primary billing</Badge>
              </div>
              {role?.isOwner && (
                <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
                  Change billing contact
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <TeamRegistrationsList team={team} />

      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-end gap-6 text-sm font-medium">
            <span>Total registered: ${(summary.total_registered ?? 0).toFixed(2)}</span>
            <span>Total paid: ${(summary.total_paid ?? 0).toFixed(2)}</span>
            <span>Outstanding: ${(summary.total_outstanding ?? 0).toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Share payment link</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex gap-2">
            <Input value={payUrl} readOnly />
            <Button variant="outline" onClick={handleCopy}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Anyone with this link can view and pay outstanding balances for this team. They do not need an account.
          </p>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change billing contact</DialogTitle>
          </DialogHeader>
          <RadioGroup value={selectedUserId ?? ''} onValueChange={setSelectedUserId}>
            {members.map((m: any) => (
              <div key={m.user_id} className="flex items-center gap-2 py-1">
                <RadioGroupItem value={m.user_id} id={m.user_id} />
                <Label htmlFor={m.user_id}>
                  {m.profile?.name ?? m.profile?.email} <span className="text-muted-foreground">({m.role})</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleChangeBilling} disabled={!selectedUserId}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
